import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, IconButton, Chip, Tab, Tabs, CircularProgress, Divider,
} from '@mui/material';
import {
  ArrowBack, Lock, LockOpen, CheckCircleOutline, WarningAmberRounded, ErrorOutline, InfoOutlined,
  ChevronRight, TrendingUp, TrendingDown, HistoryToggleOff,
} from '@mui/icons-material';
import { periodCloseService } from '../../services/periodCloseService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { ErrorState } from '../../components/common/pageKit';

const dt = (v?: string | null) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS_META: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' | 'accent' | 'sub' }> = {
  OPEN: { label: 'Open', tone: 'good' }, CLOSED: { label: 'Closed', tone: 'sub' }, LOCKED: { label: 'Locked', tone: 'sub' },
  UPCOMING: { label: 'Upcoming', tone: 'sub' }, PENDING_APPROVAL: { label: 'Pending Approval', tone: 'warn' },
  CLOSE_IN_PROGRESS: { label: 'In Progress', tone: 'warn' }, REOPENED: { label: 'Reopened', tone: 'accent' },
};
const chkTint = (s: string, c: any) => (s === 'PASS' ? c.good : s === 'FAIL' ? c.bad : c.warn);
const readinessTone = (pct: number, c: any) => (pct >= 90 ? c.good : pct >= 70 ? c.warn : c.bad);

const Period360Page: React.FC = () => {
  const { fy, period } = useParams<{ fy: string; period: string }>();
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  const load = useCallback(() => {
    if (!fy || !period) return;
    setLoading(true);
    periodCloseService.period360(fy, Number(period))
      .then((r) => { setData(r); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load period'))
      .finally(() => setLoading(false));
  }, [fy, period]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', height: 400 }}><CircularProgress /></Box>;
  if (error || !data) return <Box sx={{ p: 3 }}><ErrorState message={error || undefined} onRetry={load} /></Box>;

  const h = data.header;
  const status = h.status || (h.is_closed ? 'CLOSED' : 'OPEN');
  const sc = c[STATUS_META[status]?.tone || 'sub'];
  const readinessPct = data.summary?.closeReadiness ?? h.readiness_pct ?? 0;
  const checklist = data.checklist?.items || [];
  const exceptions = data.exceptions?.exceptions || [];
  const recon = checklist.filter((i: any) => ['BANK', 'AR', 'AP'].includes(i.category));
  const fs = data.financialSummary;
  const ap = data.approval || {};

  const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: c.sub }}>{children}</Typography>
  );
  const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Box><Label>{label}</Label><Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{value ?? '—'}</Typography></Box>
  );

  const ChecklistList: React.FC<{ items: any[] }> = ({ items }) => (
    <Stack spacing={0.75}>
      {items.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Nothing to show.</Typography>
        : items.map((it: any, i: number) => {
          const tint = chkTint(it.status, c);
          return (
            <Stack key={i} direction="row" alignItems="center" spacing={1.25} onClick={() => navigate(it.route)}
              sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', '&:hover': { bgcolor: c.panel2 } }}>
              {it.status === 'PASS' ? <CheckCircleOutline sx={{ fontSize: 18, color: tint }} />
                : it.status === 'FAIL' ? <ErrorOutline sx={{ fontSize: 18, color: tint }} />
                  : <WarningAmberRounded sx={{ fontSize: 18, color: tint }} />}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.check}</Typography>
                  {it.blocking && <Chip size="small" label="BLOCKING" sx={{ height: 16, fontSize: '0.5625rem', fontWeight: 800, bgcolor: `${c.bad}1a`, color: c.bad }} />}
                  <Chip size="small" label={it.category.replace('_', ' ')} variant="outlined" sx={{ height: 16, fontSize: '0.5625rem', borderColor: c.line }} />
                </Stack>
                <Typography variant="caption" color="text.secondary">{it.description}</Typography>
              </Box>
              {(it.count != null || it.amount != null) && <Typography variant="caption" sx={{ fontWeight: 700, color: tint, whiteSpace: 'nowrap' }}>{it.count != null ? it.count : ''}{it.count != null && it.amount ? ' · ' : ''}{it.amount ? formatIndianNumber(it.amount) : ''}</Typography>}
              <ChevronRight sx={{ fontSize: 18, color: c.sub }} />
            </Stack>
          );
        })}
    </Stack>
  );

  return (
    <Box sx={{ bgcolor: c.bg, minHeight: '100%', pb: 6 }}>
      {/* sticky header */}
      <Box sx={{ position: 'sticky', top: { xs: 32, sm: 64 }, zIndex: (t) => t.zIndex.appBar - 1, backgroundColor: c.panel, borderBottom: line, px: 3, py: 1.75 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <IconButton size="small" onClick={() => navigate('/finance/period-close')} sx={{ border: line }}><ArrowBack fontSize="small" /></IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{h.label || h.period_name}</Typography>
                <Chip label={STATUS_META[status]?.label || status} size="small" icon={status === 'CLOSED' || status === 'LOCKED' ? <Lock sx={{ fontSize: '14px !important' }} /> : <LockOpen sx={{ fontSize: '14px !important' }} />}
                  sx={{ height: 22, fontWeight: 700, bgcolor: `${sc}1f`, color: sc }} />
              </Stack>
              <Typography variant="body2" color="text.secondary">{String(h.start_date).slice(0, 10)} → {String(h.end_date).slice(0, 10)} · {h.financial_year}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">Readiness</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: readinessTone(readinessPct, c), lineHeight: 1 }}>{readinessPct}%</Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 3, pt: 2 }}>
        <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{ borderBottom: line, px: 1, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 } }}>
            {['Overview', 'Checklist', 'Reconciliations', 'Exceptions', 'Financial Summary', 'Approvals', 'Audit'].map((t) => <Tab key={t} label={t} />)}
          </Tabs>

          {/* Overview */}
          {tab === 0 && (
            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Readiness by Domain</Typography>
                  <Stack spacing={1.25}>
                    {(data.readiness?.domains || data.summary?.domains || []).map((d: any) => {
                      const tint = chkTint(d.status, c);
                      return (
                        <Box key={d.key}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>{d.score}%</Typography>
                          </Stack>
                          <Box sx={{ height: 6, borderRadius: 3, bgcolor: `${tint}22`, overflow: 'hidden' }}>
                            <Box sx={{ width: `${d.score}%`, height: '100%', bgcolor: tint }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Close Position</Typography>
                  <Grid container spacing={1.5}>
                    {[
                      { l: 'Unposted Journals', v: data.summary?.unpostedTransactions },
                      { l: 'Unreconciled Bank', v: data.summary?.unreconciledBankItems },
                      { l: 'Open Receivables', v: formatIndianNumber(data.summary?.openAR || 0) },
                      { l: 'Open Payables', v: formatIndianNumber(data.summary?.openAP || 0) },
                      { l: 'Tax Items Pending', v: data.summary?.taxItemsPending },
                      { l: 'Journal Exceptions', v: data.summary?.journalsPending },
                    ].map((x) => (
                      <Grid size={{ xs: 6, sm: 4 }} key={x.l}>
                        <Box sx={{ p: 1.5, borderRadius: 2, border: line }}>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>{x.v ?? '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{x.l}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Checklist */}
          {tab === 1 && <Box sx={{ p: 2.5 }}><ChecklistList items={checklist} /></Box>}

          {/* Reconciliations */}
          {tab === 2 && (
            <Box sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Sub-ledger vs GL and bank reconciliation checks for this period.</Typography>
              <ChecklistList items={recon} />
            </Box>
          )}

          {/* Exceptions */}
          {tab === 3 && (
            <Box sx={{ p: 2.5 }}>
              {exceptions.length === 0 ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 4 }}><CheckCircleOutline sx={{ fontSize: 30, color: c.good }} /><Typography variant="body2" sx={{ fontWeight: 600 }}>No exceptions</Typography></Stack>
              ) : (
                <Stack spacing={1}>
                  {exceptions.map((ex: any, i: number) => {
                    const tint = ex.severity === 'BLOCKING' ? c.bad : ex.severity === 'WARNING' ? c.warn : c.accent;
                    return (
                      <Stack key={i} direction="row" alignItems="center" spacing={1.25} onClick={() => navigate(ex.route)}
                        sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', '&:hover': { bgcolor: c.panel2 } }}>
                        {ex.severity === 'BLOCKING' ? <ErrorOutline sx={{ fontSize: 18, color: tint }} />
                          : ex.severity === 'WARNING' ? <WarningAmberRounded sx={{ fontSize: 18, color: tint }} /> : <InfoOutlined sx={{ fontSize: 18, color: tint }} />}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{ex.title}</Typography>
                            <Chip size="small" label={ex.severity} sx={{ height: 16, fontSize: '0.5625rem', fontWeight: 800, bgcolor: `${tint}1a`, color: tint }} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{ex.detail}</Typography>
                        </Box>
                        {!!ex.amount && <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>{formatIndianNumber(ex.amount)}</Typography>}
                        <ChevronRight sx={{ fontSize: 18, color: c.sub }} />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          )}

          {/* Financial Summary */}
          {tab === 4 && (
            <Box sx={{ p: 2.5 }}>
              <Stack divider={<Divider sx={{ borderColor: c.line }} />} spacing={0}>
                <Stack direction="row" justifyContent="space-between" sx={{ pb: 1 }}>
                  <Label>Line</Label><Stack direction="row" spacing={4}><Label>This period</Label><Label>vs previous</Label></Stack>
                </Stack>
                {[
                  { k: 'revenue', label: 'Revenue', good: 'up' }, { k: 'cogs', label: 'COGS', good: 'down' },
                  { k: 'grossProfit', label: 'Gross Profit', good: 'up' }, { k: 'opex', label: 'Operating Expenses', good: 'down' },
                  { k: 'netProfit', label: 'Net Profit', good: 'up' }, { k: 'ar', label: 'Receivables (closing)', good: null },
                  { k: 'ap', label: 'Payables (closing)', good: null }, { k: 'cash', label: 'Cash & Bank', good: 'up' },
                ].map((row) => {
                  const val = fs?.current?.[row.k] ?? 0; const delta = fs?.deltas?.[row.k];
                  const up = (delta ?? 0) >= 0; const deltaGood = row.good == null ? null : row.good === 'up' ? up : !up;
                  const deltaColor = deltaGood == null ? c.sub : deltaGood ? c.good : c.bad;
                  return (
                    <Stack key={row.k} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                      <Stack direction="row" alignItems="center" spacing={4}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 110, textAlign: 'right' }}>{formatIndianNumber(val)}</Typography>
                        <Stack direction="row" alignItems="center" sx={{ minWidth: 90, justifyContent: 'flex-end' }}>
                          {delta != null ? <>{up ? <TrendingUp sx={{ fontSize: 14, color: deltaColor }} /> : <TrendingDown sx={{ fontSize: 14, color: deltaColor }} />}<Typography variant="caption" sx={{ fontWeight: 700, color: deltaColor }}>{formatIndianNumber(Math.abs(delta))}</Typography></> : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </Stack>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
              {fs && !fs.previous && <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>No previous period — deltas unavailable.</Typography>}
            </Box>
          )}

          {/* Approvals */}
          {tab === 5 && (
            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Status" value={STATUS_META[status]?.label || status} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Readiness at close" value={`${readinessPct}%`} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Prepared by" value={ap.preparedBy} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Prepared at" value={dt(ap.preparedAt)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Reviewed by" value={ap.reviewedBy} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Approved by" value={ap.approvedBy} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Approved at" value={dt(ap.approvedAt)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><Field label="Closed at" value={dt(ap.closedAt)} /></Grid>
                {ap.reopenedAt && <>
                  <Grid size={{ xs: 12, sm: 6 }}><Field label="Reopened by" value={ap.reopenedBy} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><Field label="Reopened at" value={dt(ap.reopenedAt)} /></Grid>
                  <Grid size={{ xs: 12 }}><Field label="Reopen reason" value={ap.reopenReason} /></Grid>
                </>}
              </Grid>
            </Box>
          )}

          {/* Audit */}
          {tab === 6 && (
            <Box sx={{ p: 2.5 }}>
              {(data.activity || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No close activity recorded yet.</Typography>
              ) : (
                <Stack spacing={0}>
                  {data.activity.map((a: any, i: number) => (
                    <Stack key={a.id} direction="row" spacing={1.5}>
                      <Stack alignItems="center">
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: `${c.accent}1a`, color: c.accent, flexShrink: 0 }}><HistoryToggleOff sx={{ fontSize: 16 }} /></Box>
                        {i < data.activity.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 22, bgcolor: c.line, my: 0.25 }} />}
                      </Stack>
                      <Box sx={{ pb: i < data.activity.length - 1 ? 1.5 : 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{String(a.action).replace(/_/g, ' ')}</Typography>
                        {a.detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{a.detail}</Typography>}
                        <Typography variant="caption" color="text.secondary">{a.performed_by_name || 'System'} · {dt(a.created_at)}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Period360Page;
